import type { Environment } from '../types/Environment';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import { getEnvironmentValue } from './getEnvironmentValue';
import { ENVIRONMENT_VALUES } from '../constants/ENVIRONMENT_VALUES';
import { isValidEnvironment } from './isValidEnvironment';

/**
 * @group Functions
 * @summary <blockquote>Determines the current application environment (development, preview, or production).</blockquote>
 * 
 * @returns Result containing the current environment or an AppError if detection fails
 * 
 * @example
 * ```typescript
 * import { getEnvironment } from '@shared/utils';
 * 
 * const result = getEnvironment();
 * result.match({
 *   ok: (env) => console.log(`Current environment: ${env}`),
 *   err: (error) => console.error(`Detection failed: ${error.errorMessage}`)
 * });
 * ```
 */
export function getEnvironment(): Result<Environment, AppError> {
  // Use getEnvironmentValue for proper Metro bundler static analysis on mobile
  const vercelEnvResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.NEXT_PUBLIC_VERCEL_ENV);
  if (vercelEnvResult.isOk) {
    const envValue = vercelEnvResult.value;
    const isValid = isValidEnvironment(envValue);
    if (isValid) {
      return ok(envValue as Environment);
    }
    // If NEXT_PUBLIC_VERCEL_ENV is set but invalid, return error
    return err({
      errorCode: 'INVALID_ENVIRONMENT_FOUND',
      errorMessage: `Invalid environment value: ${envValue}`
    });
  }

  // Fallback to NODE_ENV for development environments
  const nodeEnvResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.NEXT_PUBLIC_NODE_ENV);
  if (nodeEnvResult.isOk) {
    const envValue = nodeEnvResult.value;
    const isValid = isValidEnvironment(envValue);
    if (isValid) {
      return ok(envValue as Environment);
    }
    // If NEXT_PUBLIC_NODE_ENV is set but invalid, return error
    return err({
      errorCode: 'INVALID_ENVIRONMENT_FOUND',
      errorMessage: `Invalid environment value: ${envValue}`
    });
  }

  return err({
    errorCode: 'NO_ENV_VARIABLES',
    errorMessage: 'No environment variables (NEXT_PUBLIC_VERCEL_ENV or NEXT_PUBLIC_NODE_ENV) are set'
  });
} 